import React, { useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button } from 'ncoded-component-library';
import PlusIcon from 'icons/Plus.icon';
import AddABrokerModal from '../../components/AddABrokerModal';
import { InvitedBroker } from '../../models/InvitedBroker';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import useSearchParams from 'hooks/useSearchParams';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
  LOCKED_COLUMN,
} from './components/BrokerListTableCells/data';
import { BODY_CONTENT_GENERATE } from './components/BrokerListTableCells';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useMatchMedia from 'hooks/useMatchMedia';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import useTableFetchState from 'hooks/useTableFetchState';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useBrokerListFilters from './hooks/useBrokerListFilters';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import { getHasActiveFilters, getIsArrayEmpty } from 'utils';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import ManageColumns from 'components/ManageColumns';

import './BrokerList.styles.scss';

type BrokerListProps = {
  className?: string;
};

const BrokerList: React.FC<BrokerListProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();
  const [addABrokerModal, addABrokerModalRef] = useCallbackRef(null);

  const { popServerError } = useContext(PopNotificationsContext);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: invitedBrokers,
    setIsLoading,
    setData,
    setPaginatedData,
  } = useTableFetchState<InvitedBroker>();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const classes = classNames('broker-list', className);

  const isPhablet = useMatchMedia('isPhablet');

  const searchParams = useSearchParams();

  const {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  } = useBrokerListFilters();

  const isEmpty = !isLoading && getIsArrayEmpty(invitedBrokers);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, searchParams),
    [filterOptions, searchParams],
  );

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.BROKER_LIST,
    TABLE_COLUMNS,
  );

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (broker: InvitedBroker) =>
            BODY_CONTENT_GENERATE[col]({
              broker,
            }),
        }),
        {},
      ),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.portfolio.admin.getInvitedBrokers({
          $limit: limit,
          ...searchParams,
        });

        setPaginatedData(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [searchParams, limit, popServerError, setPaginatedData, setIsLoading]);

  return (
    <div className={classes}>
      <div className="admin-dashboard-router__heading">
        <h1>{t('brokerList')}</h1>
        <div className="broker-list__filters">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />
              <MultiSortPanel
                ref={multiSortRef}
                options={translatedSortOptions}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}
          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
          <Button icon={PlusIcon} onClick={() => addABrokerModal?.open()}>
            {t(isPhablet ? 'add' : 'addABroker')}
          </Button>
        </div>
      </div>
      <ActiveFilterSortPills
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
        filterOptions={filterOptions}
        sortOptions={translatedSortOptions}
      />
      <ReorderResizeTable
        columns={columns}
        data={invitedBrokers}
        isDataLoading={isLoading}
        totalPages={totalPages}
        totalItems={totalItems}
        recordName="broker"
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        sortingValues={SORTING_VALUES}
        columnFilterOptions={columnFilterOptions}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder as (columns: Array<keyof InvitedBroker>) => void}
      />

      <AddABrokerModal
        ref={addABrokerModalRef}
        addInvitedBroker={(broker) => setData((old) => [...old, broker])}
      />
    </div>
  );
};

export default BrokerList;
